import { FieldValues, Path } from "react-hook-form";
import { nameof } from "ts-simple-nameof";

import { DomesticConstants } from "@assets/constants";
import { DomesticTypes, CommonTypes } from "@assets/types";

// --- Domestic Kiosk
export interface DomesticKioskForm extends FieldValues {
  BookingNo: string;
}
export const defaultDomesticKioskFormValues: DomesticKioskForm = {
  BookingNo: "",
};

// --- Create booking detail packages
export interface CargoDetailForm {
  id: CommonTypes.UUID; // Only used for the action table
  Type: DomesticTypes.DomesticItemCondition | null;
  Description: string;
  Quantity: number | null;
  Weight: number | null;
  WeightConfirmation: boolean;
  Volume: number | null;
  Fragile: boolean;
  Hazardous: boolean;
  HazardousMaterial: DomesticTypes.DomesticHazardousMaterial | null;
}

export const defaultCargoDetailForm: CargoDetailForm = {
  id: `a-b-c-d-e`,
  Type: null,
  Description: "",
  Quantity: null,
  Weight: null,
  WeightConfirmation: false,
  Volume: null,
  Fragile: false,
  Hazardous: false,
  HazardousMaterial: null,
};

// --- Create booking
export interface CreateForm extends FieldValues {
  IsChristmasPackage: boolean;
  BookingParty: DomesticConstants.BOOKING_PARTY;
  // SENDER
  SenderPartyType: DomesticConstants.BOOKING_PARTY_TYPE;
  SenderCustomer: DomesticTypes.DomesticCustomer | null;
  SenderSSN: string;
  SenderName: string;
  SenderIsNewAddress: boolean;
  SenderDepartment: DomesticTypes.DomesticDepartment | null;
  SenderAddress: string;
  SenderPostalCode: DomesticTypes.DomesticPostCode | null;
  SenderPhone: string;
  SenderEmail: string;
  // Receiver
  ReceiverPartyType: DomesticConstants.BOOKING_PARTY_TYPE;
  ReceiverCustomer: DomesticTypes.DomesticCustomer | null;
  ReceiverSSN: string;
  ReceiverName: string;
  ReceiverIsNewAddress: boolean;
  ReceiverDepartment: DomesticTypes.DomesticDepartment | null;
  ReceiverAddress: string;
  ReceiverPostalCode: DomesticTypes.DomesticPostCode | null;
  ReceiverPhone: string;
  ReceiverEmail: string;
  // Payer
  IsSeparatePayerLegs: boolean;
  Payer: DomesticConstants.BOOKING_PARTY | null;
  // PickUp
  PickupMethod: DomesticConstants.DELIVERY_MODE;
  ReceiptDate: Date | null;
  // DropOff
  DropOffMethod: DomesticConstants.DELIVERY_MODE;
  DeliveryDate: Date | null;
  // Details
  CargoDetails: CargoDetailForm[];
  // Comment
  Comment: string;
}

export const defaultFormValues: CreateForm = {
  IsChristmasPackage: false,
  BookingParty: DomesticConstants.BOOKING_PARTY.SENDER,
  // SENDER
  SenderPartyType: DomesticConstants.BOOKING_PARTY_TYPE.COMPANY,
  SenderCustomer: null,
  SenderSSN: "",
  SenderDepartment: null,
  SenderIsNewAddress: false,
  SenderName: "",
  SenderAddress: "",
  SenderPostalCode: null,
  SenderPhone: "",
  SenderEmail: "",
  // Receiver
  ReceiverPartyType: DomesticConstants.BOOKING_PARTY_TYPE.COMPANY,
  ReceiverCustomer: null,
  ReceiverSSN: "",
  ReceiverIsNewAddress: false,
  ReceiverDepartment: null,
  ReceiverName: "",
  ReceiverAddress: "",
  ReceiverPostalCode: null,
  ReceiverPhone: "",
  ReceiverEmail: "",
  // Payer
  IsSeparatePayerLegs: false,
  Payer: null,
  // PickUp
  PickupMethod: DomesticConstants.DELIVERY_MODE.DEPOT,
  ReceiptDate: null,
  // DropOff
  DropOffMethod: DomesticConstants.DELIVERY_MODE.DEPOT,
  DeliveryDate: null,
  // Details
  CargoDetails: [],
  // Comment
  Comment: "",
};

export interface BookingPartySectionFields {
  BookingPartyType: Path<CreateForm>;
  Customer: Path<CreateForm>;
  SSN: Path<CreateForm>;
  Name: Path<CreateForm>;
  IsNewAddress: Path<CreateForm>;
  Department: Path<CreateForm>;
  Address: Path<CreateForm>;
  PostalCode: Path<CreateForm>;
  Phone: Path<CreateForm>;
  Email: Path<CreateForm>;
}

// To help with choosing the right field names the inputs/selects were registered with
export const bookingPartyFieldNames: Record<
  | DomesticConstants.BOOKING_PARTY.SENDER
  | DomesticConstants.BOOKING_PARTY.RECEIVER,
  BookingPartySectionFields
> = {
  SENDER: {
    BookingPartyType: nameof<CreateForm>((item) => item.SenderPartyType),
    Customer: nameof<CreateForm>((item) => item.SenderCustomer),
    SSN: nameof<CreateForm>((item) => item.SenderSSN),
    IsNewAddress: nameof<CreateForm>((item) => item.SenderIsNewAddress),
    Department: nameof<CreateForm>((item) => item.SenderDepartment),
    Name: nameof<CreateForm>((item) => item.SenderName),
    Address: nameof<CreateForm>((item) => item.SenderAddress),
    PostalCode: nameof<CreateForm>((item) => item.SenderPostalCode),
    Phone: nameof<CreateForm>((item) => item.SenderPhone),
    Email: nameof<CreateForm>((item) => item.SenderEmail),
  },
  RECEIVER: {
    BookingPartyType: nameof<CreateForm>((item) => item.ReceiverPartyType),
    Customer: nameof<CreateForm>((item) => item.ReceiverCustomer),
    SSN: nameof<CreateForm>((item) => item.ReceiverSSN),
    IsNewAddress: nameof<CreateForm>((item) => item.ReceiverIsNewAddress),
    Department: nameof<CreateForm>((item) => item.ReceiverDepartment),
    Name: nameof<CreateForm>((item) => item.ReceiverName),
    Address: nameof<CreateForm>((item) => item.ReceiverAddress),
    PostalCode: nameof<CreateForm>((item) => item.ReceiverPostalCode),
    Phone: nameof<CreateForm>((item) => item.ReceiverPhone),
    Email: nameof<CreateForm>((item) => item.ReceiverEmail),
  },
};

// PLACE OF RECEIPT and PLACE OF DELIVERY section field names
export interface PlacesSectionFields {
  ContactName: Path<CreateForm>;
  ContactPhone: Path<CreateForm>;
  Method: Path<CreateForm>;
  Date: Path<CreateForm>;
  CustomerAddressField: Path<CreateForm>;
  CustomerPostalCodeField: Path<CreateForm>;
  CustomerPhoneField: Path<CreateForm>;
}

export const placeFieldNames: Record<"PLR" | "PFD", PlacesSectionFields> = {
  PLR: {
    ContactName: nameof<CreateForm>((item) => item.PickupContactName),
    ContactPhone: nameof<CreateForm>((item) => item.PickupContactPhone),
    Method: nameof<CreateForm>((item) => item.PickupMethod),
    Date: nameof<CreateForm>((item) => item.ReceiptDate),
    CustomerAddressField: nameof<CreateForm>((item) => item.SenderAddress),
    CustomerPostalCodeField: nameof<CreateForm>(
      (item) => item.SenderPostalCode
    ),
    CustomerPhoneField: nameof<CreateForm>((item) => item.SenderPhone),
  },
  PFD: {
    ContactName: nameof<CreateForm>((item) => item.DropOffContactName),
    ContactPhone: nameof<CreateForm>((item) => item.DropOffContactPhone),
    Method: nameof<CreateForm>((item) => item.DropOffMethod),
    Date: nameof<CreateForm>((item) => item.DeliveryDate),
    CustomerAddressField: nameof<CreateForm>((item) => item.ReceiverAddress),
    CustomerPostalCodeField: nameof<CreateForm>(
      (item) => item.ReceiverPostalCode
    ),
    CustomerPhoneField: nameof<CreateForm>((item) => item.ReceiverPhone),
  },
};
